import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Music } from "lucide-react";

export default function ThemeMusic({ lifted }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = 0.55;
    audio.loop = true;
    audio
      .play()
      .then(() => setPlaying(true))
      .catch(() => setBlocked(true));
    const onError = () => setMissing(true);
    audio.addEventListener("error", onError);
    return () => audio.removeEventListener("error", onError);
  }, []);

  useEffect(() => {
    const stepAside = () => {
      const audio = audioRef.current;
      if (audio && !audio.paused) {
        audio.pause();
        setPlaying(false);
      }
    };
    window.addEventListener("cgd:episode-audio", stepAside);
    return () => window.removeEventListener("cgd:episode-audio", stepAside);
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio
        .play()
        .then(() => {
          setPlaying(true);
          setBlocked(false);
        })
        .catch(() => setBlocked(true));
    }
  };

  if (missing) return null;

  return (
    <>
      <audio ref={audioRef} src="/audio/theme.mp3" preload="auto" loop />
      <motion.button
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.2 }}
        onClick={toggle}
        data-testid="theme-music-toggle"
        aria-label={playing ? "Turn theme music off" : "Turn theme music on"}
        className={`fixed left-4 z-50 flex items-center gap-2.5 rounded-full border border-white/15 glass px-4 py-2.5 transition-all duration-300 hover:scale-[1.04] hover:border-cherry ${
          lifted ? "bottom-[84px]" : "bottom-5"
        }`}
      >
        {playing ? (
          <span className="flex items-end gap-[2px]" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="w-[3px] rounded-full bg-cherry animate-eq"
                style={{ height: 12, animationDelay: `${i * 0.15}s` }}
              />
            ))}
          </span>
        ) : (
          <Music
            className={`h-3.5 w-3.5 text-cherry ${blocked ? "animate-pulse-dot" : ""}`}
          />
        )}
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/80">
          {playing ? "Sound on · CGD theme" : "Play theme song"}
        </span>
      </motion.button>
    </>
  );
}
